import { useCallback } from 'react';
import { useAuth } from 'oidc-react';
import { useQueryClient } from '@tanstack/react-query';
import { ApiFetchParams, getQueryFn, useApiQuery } from '../helpers/useApi';
import { apiFetch } from './fetch';

const CUSTOM_TIME_PERIODS_PATH = '/data/periods/v1/custom-time-periods';
const CUSTOM_TIME_PERIODS_QUERY_KEY = 'customTimePeriods';

export type CustomTimePeriod = {
  id: string;
  name: string;
  startDate: Date;
  endDate: Date;
};

export type CustomTimePeriodCreate = {
  name: string;
  startDate: Date;
  endDate: Date;
};

const parseCustomTimePeriod = (period: CustomTimePeriod): CustomTimePeriod => ({
  ...period,
  startDate: new Date(period.startDate),
  endDate: new Date(period.endDate),
});

export function useCustomTimePeriods() {
  return useApiQuery<CustomTimePeriod[]>(
    {
      path: CUSTOM_TIME_PERIODS_PATH,
      body: {},
    } as ApiFetchParams,
    CUSTOM_TIME_PERIODS_QUERY_KEY,
    {
      select: (data) => data.map(parseCustomTimePeriod),
    }
  );
}

export function useCreateCustomTimePeriod() {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const maybeAccessToken = auth?.userData?.access_token;

  return useCallback(
    async (period: CustomTimePeriodCreate) => {
      const response = await getQueryFn<CustomTimePeriod>(
        {
          path: CUSTOM_TIME_PERIODS_PATH,
          method: 'POST',
          body: period,
        },
        (
          body: unknown = {},
          path = '/',
          method = 'GET',
          endpoint: string | undefined = process.env.REACT_APP_API_GATEWAY,
          authorization: string | undefined = `Bearer ${maybeAccessToken}`
        ) => apiFetch<CustomTimePeriod>(path, body, method, endpoint, authorization)
      )();
      const newCustomTimePeriod = parseCustomTimePeriod(response);
      queryClient.setQueryData<CustomTimePeriod[]>(
        [CUSTOM_TIME_PERIODS_QUERY_KEY],
        (old = []) => [...old, newCustomTimePeriod]
      );
      return newCustomTimePeriod;
    },
    [maybeAccessToken, queryClient]
  );
}

export function useUpdateCustomTimePeriod() {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const maybeAccessToken = auth?.userData?.access_token;

  return useCallback(
    async (periodId: string, period: CustomTimePeriodCreate) => {
      const response = await getQueryFn<CustomTimePeriod>(
        {
          path: `${CUSTOM_TIME_PERIODS_PATH}/${periodId}`,
          method: 'PUT',
          body: period,
        },
        (
          body: unknown = {},
          path = '/',
          method = 'GET',
          endpoint: string | undefined = process.env.REACT_APP_API_GATEWAY,
          authorization: string | undefined = `Bearer ${maybeAccessToken}`
        ) => apiFetch<CustomTimePeriod>(path, body, method, endpoint, authorization)
      )();
      const updatedCustomTimePeriod = parseCustomTimePeriod(response);
      queryClient.setQueryData<CustomTimePeriod[]>(
        [CUSTOM_TIME_PERIODS_QUERY_KEY],
        (old = []) => old.map((ctp) => (ctp.id === periodId ? updatedCustomTimePeriod : ctp))
      );
      return updatedCustomTimePeriod;
    },
    [maybeAccessToken, queryClient]
  );
}

export function useDeleteCustomTimePeriod() {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const maybeAccessToken = auth?.userData?.access_token;

  return useCallback(
    async (periodId: string) => {
      await getQueryFn<object>(
        {
          path: `${CUSTOM_TIME_PERIODS_PATH}/${periodId}`,
          method: 'DELETE',
          body: {},
        },
        (
          body: unknown = {},
          path = '/',
          method = 'GET',
          endpoint: string | undefined = process.env.REACT_APP_API_GATEWAY,
          authorization: string | undefined = `Bearer ${maybeAccessToken}`
        ) => apiFetch<object>(path, body, method, endpoint, authorization)
      )();
      queryClient.setQueryData<CustomTimePeriod[]>(
        [CUSTOM_TIME_PERIODS_QUERY_KEY],
        (old = []) => old.filter((ctp) => ctp.id !== periodId)
      );
    },
    [maybeAccessToken, queryClient]
  );
}
